<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import ClassScheduleDialog from '@/components/utilities/ClassScheduleDialog.vue';
import UiTermSelect from '@/components/ui/UiTermSelect.vue';
import { apiRequest } from '@/api/http';
import { useCopySchedule } from '@/composables/useCopySchedule';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useTerm } from '@/composables/useTerm';
import { WEEKDAYS, formatTimeRange } from '@/utils/format';

useSessionGuard();

// The weekly class periods of the adviser's own section.
const store = useStore();
const year = computed(() => store.getters.getFilterSelected);
const canAdd = computed(() => store.state.user?.user?.isAdminApproved == 1);

// Shown (as info, not an error) to teachers who don't advise a section.
const NO_ADVISORY = {
  title: 'No advisory section yet',
  text: 'You don\'t advise a section in this school year. An admin assigns advisers under Rooms; once you have one, its schedule shows here.',
};
// Advisers are per school year, so the section follows the header's year.
const section = shallowRef(null);
const hasNoSection = shallowRef(false);
const sectionError = shallowRef('');
async function loadSection() {
  section.value = null;
  hasNoSection.value = false;
  sectionError.value = '';
  try {
    const row = await apiRequest('get', `/rooms-section/getMyAdvisorySection/${store.state.user.id}`, null, {
      params: { schoolYearId: year.value },
    });
    if (row?.id) section.value = row;
    else hasNoSection.value = true;
  } catch {
    sectionError.value = 'Could not load your advisory section.';
  }
}
watch(year, loadSection, { immediate: true });

// Each term has its own schedule.
const { hasTerms, terms, term, termQuery } = useTerm();
const { items, loading, error, load } = useListResource(() =>
  section.value
    ? apiRequest(
        'get',
        `/enroll-student/getClassProgramm/${encodeURIComponent(section.value.grade_level)}/${section.value.id}/${year.value}?${termQuery.value}`,
      )
    : [],
);
// The year reloads the section, which reloads this.
watch([section, term], load);

const headers = [
  { title: 'Time', key: 'time', sortable: false },
  { title: 'Teacher', key: 'name' },
  { title: 'Subject', key: 'subject_title' },
];
const groupBy = [{ key: 'day', order: 'asc' }];
const dayIndex = (d) => (WEEKDAYS.includes(d) ? WEEKDAYS.indexOf(d) : 99);
const customKeySort = { day: (a, b) => dayIndex(a) - dayIndex(b) };

// A section with no periods this year (in the chosen term) can start from
// last year's.
const { copying, skipped, skippedOpen, copy } = useCopySchedule();
const canCopy = computed(
  () => canAdd.value && !!section.value && !loading.value && !error.value && items.value.length === 0,
);
async function copyFromLastYear() {
  if (await copy(section.value.id, year.value, term.value)) load();
}

const editing = shallowRef(null);
const formOpen = shallowRef(false);
function openForm(period = null) {
  editing.value = period;
  formOpen.value = true;
}
</script>

<template>
  <UiTableCard
    title="Advisory schedule"
    :description="section ? `${section.grade_level} · ${section.room_section}` : 'The weekly schedule of your advisory section.'"
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error || sectionError"
    :info="hasNoSection ? NO_ADVISORY : null"
    :searchable="false"
    :items-per-page="-1"
    :group-by="groupBy"
    groups-open
    :custom-key-sort="customKeySort"
    :add-label="canAdd && section ? 'Add period' : ''"
    :empty-text="`No class periods${term ? ` in the ${term}` : ''} yet.`"
    row-label="subject_title"
    :row-actions="(period) => [{ title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(period) }]"
    @add="openForm()"
  >
    <template v-if="hasTerms" #toolbar>
      <UiTermSelect v-model="term" :items="terms" />
    </template>
    <template v-if="canCopy" #actions>
      <v-btn variant="outlined" prepend-icon="mdi-content-copy" :loading="copying" @click="copyFromLastYear">
        Copy from last school year
      </v-btn>
    </template>
    <template #[`item.time`]="{ item }">
      <span class="text-no-wrap">{{ item.time ? formatTimeRange(item.time) : formatTimeRange(item.times_slot_from, item.times_slot_to) }}</span>
    </template>
  </UiTableCard>

  <ClassScheduleDialog
    v-if="section"
    v-model="formOpen"
    :item="editing"
    :grade="section.grade_level"
    :section="section"
    :periods="items"
    add-endpoint="/enroll-student/addSchedule"
    :term="term"
    @saved="load"
  />

  <UiDialog
    v-model="skippedOpen"
    title="Some periods weren't copied"
    description="Add these by hand, or fix the clash and copy again."
    size="lg"
    :persistent="false"
  >
    <ul class="copy-skipped">
      <li v-for="(p, i) in skipped" :key="i">
        <span class="font-weight-medium">{{ p.day }} {{ p.time }} · {{ p.subject }}</span>
        <span class="text-medium-emphasis"> ({{ p.teacher }}) — {{ p.reason }}</span>
      </li>
    </ul>
    <template #footer="{ close }">
      <v-btn color="primary" @click="close">OK</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.copy-skipped {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.8;
}
</style>

